/**
 * Analytics Tracker
 *
 * Handles tracking of popup impressions and dismissals via AJAX.
 * Submissions are tracked server-side via Gravity Forms hooks.
 */

import { getOrCreateVisitorId } from './visitor-id';
import { createLogger } from '../../shared/logger';

const logger = createLogger('AnalyticsTracker');

/**
 * Event types that can be tracked from the frontend.
 */
export type TrackableEventType = 'impression' | 'dismissal';

/**
 * Data sent with each tracking request.
 */
interface TrackingData {
	feed_id: number;
	form_id: number;
	event_type: TrackableEventType;
	page_url: string;
	referrer: string;
	device_type: string;
	visitor_id: string;
}

/**
 * Configuration for the analytics tracker.
 */
export interface AnalyticsConfig {
	enabled: boolean;
	ajaxUrl: string;
	action: string;
	nonce: string;
}

/**
 * Detect device type from screen size and user agent.
 *
 * @return Device type: 'mobile', 'tablet', or 'desktop'.
 */
function detectDeviceType(): string {
	const ua = navigator.userAgent.toLowerCase();

	// Check for tablets first
	const tabletPatterns = ['tablet', 'ipad', 'playbook', 'silk', 'kindle'];
	for (const pattern of tabletPatterns) {
		if (ua.includes(pattern)) {
			return 'tablet';
		}
	}

	// Check for mobile devices
	const mobilePatterns = [
		'mobile',
		'android',
		'iphone',
		'ipod',
		'blackberry',
		'windows phone',
		'opera mini',
		'opera mobi',
	];
	for (const pattern of mobilePatterns) {
		if (ua.includes(pattern)) {
			return 'mobile';
		}
	}

	// Also check screen width as a fallback
	if (window.innerWidth < 768) {
		return 'mobile';
	} else if (window.innerWidth < 1024) {
		return 'tablet';
	}

	return 'desktop';
}

/**
 * Analytics tracker class.
 *
 * Tracks popup impressions and dismissals by sending AJAX requests
 * to the WordPress backend.
 */
export class AnalyticsTracker {
	private config: AnalyticsConfig;
	private visitorId: string;
	private deviceType: string;

	constructor(config: AnalyticsConfig) {
		this.config = config;
		this.visitorId = getOrCreateVisitorId();
		this.deviceType = detectDeviceType();

		logger.log('AnalyticsTracker initialized', {
			enabled: config.enabled,
			visitorId: this.visitorId,
			deviceType: this.deviceType,
		});
	}

	/**
	 * Track a popup impression.
	 *
	 * @param feedId - The feed ID.
	 * @param formId - The form ID.
	 */
	public trackImpression(feedId: number, formId: number): void {
		this.sendEvent('impression', feedId, formId);
	}

	/**
	 * Track a popup dismissal.
	 *
	 * @param feedId - The feed ID.
	 * @param formId - The form ID.
	 */
	public trackDismissal(feedId: number, formId: number): void {
		this.sendEvent('dismissal', feedId, formId);
	}

	/**
	 * Send a tracking event to the server.
	 *
	 * @param eventType - The type of event.
	 * @param feedId    - The feed ID.
	 * @param formId    - The form ID.
	 */
	private sendEvent(
		eventType: TrackableEventType,
		feedId: number,
		formId: number
	): void {
		if (!this.config.enabled) {
			logger.log('Analytics disabled, skipping event', { eventType });
			return;
		}

		const data: TrackingData = {
			feed_id: feedId,
			form_id: formId,
			event_type: eventType,
			page_url: window.location.href,
			referrer: document.referrer,
			device_type: this.deviceType,
			visitor_id: this.visitorId,
		};

		logger.log('Sending tracking event', data);

		// Use sendBeacon for reliability on page unload, fall back to fetch
		const formData = new FormData();
		formData.append('action', this.config.action);
		formData.append('nonce', this.config.nonce);

		for (const [key, value] of Object.entries(data)) {
			formData.append(key, String(value));
		}

		// Try sendBeacon first (works during page unload)
		if (navigator.sendBeacon) {
			const sent = navigator.sendBeacon(this.config.ajaxUrl, formData);
			if (sent) {
				logger.log('Event sent via sendBeacon');
				return;
			}
		}

		// Fall back to fetch
		fetch(this.config.ajaxUrl, {
			method: 'POST',
			body: formData,
			keepalive: true, // Allows request to outlive the page
		})
			.then((response) => {
				if (response.ok) {
					logger.log('Event sent via fetch');
				} else {
					logger.warn('Failed to send event', response.status);
				}
			})
			.catch((error) => {
				logger.warn('Error sending event', error);
			});
	}

	/**
	 * Check if analytics is enabled.
	 *
	 * @return True if analytics is enabled.
	 */
	public isEnabled(): boolean {
		return this.config.enabled;
	}
}

/**
 * Create an analytics tracker instance from the global config.
 *
 * @return AnalyticsTracker instance or null if not configured.
 */
export function createAnalyticsTracker(): AnalyticsTracker | null {
	const config = window.gpPopupsAnalytics;

	if (!config) {
		logger.log('No analytics config found');
		return null;
	}

	if (!config.enabled) {
		logger.log('Analytics disabled in config');
		return null;
	}

	return new AnalyticsTracker(config);
}
